import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {EntityType} from 'common/report/util/saved-entities/types';
import {SearchDisplayContext} from 'common/report/util/search/types';

export enum HeaderAction {
  Delete = `delete`,
  Edit = `edit`,
  Refresh = `refresh`,
  Save = `save`,
  SaveNew = `save-new`,
  // TODO: rename
  Load = `open`,
  Reset = `new`,

  CreatedBy = `created-by`,
  Share = `share`,

  // alerts
  CreateAlert = `create-alert`,
  ManageAlert = `manage-alert`,
  EnableAlerts = `enable-alerts`,

  QuerySampling = `query-sampling`,
}

export interface QueryReportHeaderAttrs {
  'auto-refresh-interval-ms': number | null;
  'computed-at': string;
  'control-items': Obj[];
  dashboard?: DashboardOrigin;
  'display-context': SearchDisplayContext;
  'empty-bookmark-name': string;
  'entities-loading': boolean;
  'entity-type': string;
  'has-unsaved-changes': boolean;
  'hide-actions': HeaderAction[];
  'invalid-entity': boolean;
  'invalid-message': string;
  'is-public-dashboard'?: boolean;
  'is-save-failed': boolean;
  'num-custom-alerts': number;
  'num-dashboards': number;
  'overflow-control-items': Obj;
  'project-as-of-date': string;
  'project-id': number;
  'selected-bookmark-description': string;
  'selected-bookmark-id': number | null;
  'selected-bookmark-name': string;
  'selected-bookmark-on-dashboard-id'?: number | null;
  'selected-bookmark-user-email': string | null;
  'selected-bookmark-user-id': number | null;
  'selected-bookmark-user-name': string | null;
  'track-name': string;
  'user-id': number;
  'workspace-id': number;

  // permissions
  'can-write': boolean;
  'can-save': boolean;
  'can-share': boolean;
  'is-superadmin': boolean;
  'allow-staff-override': boolean;

  // sharing-disabled (visibility controls in create-entity-modal)
  'visibility-controls-message': string;

  // query sampling
  'sampling-factor': number;
  'is-query-sampling-enabled': boolean;
  'is-query-sampling-action-visible': boolean;
  'query-sampling-disabled-message': string;

  // custom-alerts
  'is-custom-alerts-action-visible': boolean;
  'enable-custom-alert-condition-types': boolean;
  'results-keys-and-values': Obj;
  'custom-alert-time-unit': string;
  'custom-alerts-disabled-text': string;
}

export const queryReportHeaderAttrsSchema = {
  'project-id': `number`,
  'entity-type': {
    type: `string`,
    enum: Object.values(EntityType),
    default: EntityType.Report,
  },
  'selected-bookmark-id': `number`,
  'selected-bookmark-user-id': `number`,
  'selected-bookmark-user-name': `string`,
  'selected-bookmark-user-email': `string`,
  'selected-bookmark-name': `string`,
  'selected-bookmark-description': `string`,
  'selected-bookmark-on-dashboard-id': {
    type: `number`,
    description: `Exists if bookmark is dashboard compatible. Equivalent to selected-bookmark-id except for funnels. Exists to query dashboards for matching bookmark, but reg funnels bookmarks don't exist on dashboard, only funnels query`,
  },
  'invalid-entity': {
    type: `boolean`,
    description: `If entity is invalid, save will be prevented - create new or edit`,
    default: false,
  },
  'invalid-message': {
    type: `string`,
    description: `Optional tooltip to display above disabled "Save" button when entity is invalid`,
  },
  'is-public-dashboard': `boolean`,
  'entities-loading': `boolean`,
  'computed-at': `string`,
  'control-items': {
    type: `json`,
    default: [],
    description: `Additional set of items to be rendered in the control bar, interface ControlItem[]`,
  },
  'display-context': {
    type: `string`,
    enum: Object.values(SearchDisplayContext),
    description: `display context for the load entity modal: insights, flows, dashboards, etc (SearchDisplayContext)`,
  },
  'empty-bookmark-name': `string`,
  'has-unsaved-changes': `boolean`,
  'num-custom-alerts': `number`,
  'num-dashboards': `number`,
  'overflow-control-items': {
    type: `json`,
    default: [],
    description: `Items to be rendered in the overflow section of the control bar, interface ControlItem[]`,
  },
  'project-as-of-date': {type: `string`, description: `as of date for projects with data in the past.`},
  'track-name': {
    type: `string`,
    description: `If specified, dispatches '[mp-query-report-header] <event>' bubbled events that can be listened for tracking with tab=<track-name> as a property`,
  },
  'user-id': `number`,
  'workspace-id': `number`,
  'hide-actions': {
    type: `json`,
    tsType: `HeaderAction[]`,
    description: `Hide a set of built-in header controls.`,
    default: [],
  },
  dashboard: {
    type: `json`,
  },

  // permissions
  'can-write': {
    type: `boolean`,
    description: `Can the user create/change entities?`,
    default: false,
  },
  'can-save': {
    type: `boolean`,
    description: `Can the user change the currently selected entity?`,
    default: false,
  },
  'can-share': {
    type: `boolean`,
    description: `Can the user share the currently selected entity?`,
    default: false,
  },
  'is-superadmin': `boolean`,
  'allow-staff-override': `boolean`,

  // sharing-disabled (visibility controls in create-entity-modal)
  'visibility-controls-message': {
    type: `string`,
    description: `Message to display above visibility controls`,
  },
  'is-save-failed': {
    type: `boolean`,
    description: `Save report failed`,
    default: false,
  },
  'auto-refresh-interval-ms': {
    type: `number`,
    description: `If set, creates an interval that fires a refresh event every x milliseconds.`,
    default: null,
  },

  // query sampling
  'sampling-factor': `number`,
  'is-query-sampling-enabled': `boolean`,
  'is-query-sampling-action-visible': `boolean`,
  'query-sampling-disabled-message': `string`,

  // custom-alerts
  'is-custom-alerts-action-visible': `boolean`,
  'enable-custom-alert-condition-types': `boolean`,
  'results-keys-and-values': `json`,
  'custom-alert-time-unit': `string`,
  'custom-alerts-disabled-text': `string`,
};

export enum SaveModes {
  SaveNew = `save-new`,
  Edit = `edit`,
  Save = `save`,
}
